import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import Annotation from "~/components/build/Show/components/Annotation";
import { JobAnnotationLink } from "~/components/build/Show/components/annotations/JobAnnotationLink";
import {
  FilteredAnnotationsEmptyState,
  NoAnnotationsEmptyState,
} from "~/components/build/Show/components/AnnotationsEmptyState";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { allAnnotationsQueryOptions } from "~/components/build/Show/lib/queries/allAnnotations";
import {
  type Annotation as AnnotationType,
  type AnnotationScope,
} from "~/components/build/Show/lib/types";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import Spinner from "~/components/shared/Spinner";

function scopesToScope(scopes: AnnotationScope[]): AnnotationScope | null {
  // Empty selection or both scopes selected → no scope filter (return both from the API).
  if (scopes.length === 0 || scopes.length === 2) {
    return null;
  }
  return scopes[0];
}

function AnnotationItem({ annotation }: { annotation: AnnotationType }) {
  if (annotation.scope === "job" && annotation.jobUuid) {
    return (
      <div>
        <JobAnnotationLink annotation={annotation} />
        <Annotation annotation={annotation} />
      </div>
    );
  }
  return <Annotation annotation={annotation} />;
}

type Props = {
  scopes: AnnotationScope[];
  totalAnnotationsCount: number;
};

export default function AnnotationsList({ scopes, totalAnnotationsCount }: Props) {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();
  const scope = scopesToScope(scopes);

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    allAnnotationsQueryOptions(build, scope, { placeholderData: keepPreviousData }),
  );

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const annotations = useMemo(() => {
    return (data?.pages || []).flatMap((page) => page.records);
  }, [data?.pages]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center space-x-2">
        <Spinner className="h-4 w-4" />
        <span>Fetching annotations...</span>
      </div>
    );
  }

  if (annotations.length === 0) {
    // Differentiate between "build genuinely has no annotations" and "filter hides them all".
    if (totalAnnotationsCount === 0) {
      return <NoAnnotationsEmptyState />;
    }
    return <FilteredAnnotationsEmptyState />;
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-y-1.5" onClick={navigateClickHandler}>
      {annotations.map((annotation) => (
        <AnnotationItem key={annotation.id} annotation={annotation} />
      ))}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
